import {
    createStore,
    applyMiddleware,
    compose,
    combineReducers,
    Store,
    StoreEnhancerStoreCreator,
    ReducersMapObject,
    StoreEnhancer,
} from "redux";
import thunk from "redux-thunk";
import { routerReducer, routerMiddleware } from "react-router-redux";
import moment from "moment";
import { History } from "history";
import * as StoreModule from "store";
import { ApplicationState, reducers } from "store";
import { registerSignalR } from "./register-signal-r";
import {
    COOKIE_USER_EXPIRE_DAYS,
    COOKIE_USER_JWT,
    NavigationComponents,
    SvgCalendarLayout,
} from "ui/constants";
import { getCultureFromInitialState } from "./helper";
import {
    CustomImage,
    ImageDetails,
    PostData,
    RubberBandItem,
    SpiralItem,
    TextInformation,
} from "burde";
import { getDefaultCoverTextByCalendarSizeAndCalendarType } from "page/calendar/helper";
import Cookies from "js-cookie";
import { CalendarType } from "svg/constants";

export default function configureStore(
    history: History,
    initialState?: ApplicationState,
) {
    // Build middleware. These are functions that can process the actions before they reach the store.
    const windowIsDefined =
        typeof window === "undefined" ? null : (window as any);

    // If devTools is installed, connect to it
    const devToolsExtension =
        windowIsDefined &&
        (windowIsDefined.__REDUX_DEVTOOLS_EXTENSION__ as () => StoreEnhancer);

    const createStoreWithMiddleware = compose(
        applyMiddleware(thunk, routerMiddleware(history)),
        devToolsExtension
            ? devToolsExtension()
            : <S>(next: StoreEnhancerStoreCreator<S>) => next,
    )(createStore);

    // Combine all reducers and instantiate the app-wide store instance
    const allReducers = buildRootReducer(reducers);
    const store = (createStoreWithMiddleware as any)(
        allReducers,
        initialState,
    ) as Store<ApplicationState>;

    // Enable Webpack hot module replacement for reducers
    if (module.hot) {
        module.hot.accept("./", () => {
            const nextRootReducer = require<typeof StoreModule>("./");
            store.replaceReducer(buildRootReducer(nextRootReducer.reducers));
        });
    }

    registerSignalR(store, getCultureFromInitialState(initialState));

    return store;
}

function buildRootReducer(allReducers: ReducersMapObject) {
    return combineReducers<ApplicationState>(
        Object.assign({}, allReducers, {
            routing: routerReducer,
        }) as any,
    );
}

export function initializeStoreWithConfigurationOptions(
    history: History,
    configurationOptions: any,
    calendarDefaults: any,
    initialState: ApplicationState,
    postData: PostData | null,
    initialCalendarLayout: SvgCalendarLayout | null,
    initialFrontCoverImage: ImageDetails | null,
    initialCustomCoverImage: CustomImage | null,
    initialBackCoverImage: ImageDetails | null,
    initialCustomBackImage: CustomImage | null,
    initialBorderImage: ImageDetails | null,
    initialSpiral: SpiralItem | null,
    initialRubberBand: RubberBandItem | null,
    jwt: string | null,
    initialFrontCoverSectionId: number | null,
    initialBackCoverSectionId: number | null,
    showLoginModal: boolean,
    fastTrack: boolean,
    initialFontId: number | null,
    domain: string,
) {
    // Culture can't be set by postdata as of now.
    initialState.calendar.culture = configurationOptions.culture;
    initialState.calendar.calendarType = configurationOptions.calendarType;
    initialState.calendar.calendarSize = configurationOptions.calendarSize.toUpperCase();
    initialState.event.postData = postData;
    initialState.calendar.fontId = initialFontId;
    initialState.ui.domain = domain;

    if (initialCalendarLayout) {
        initialState.calendar.layoutId = initialCalendarLayout.id;
        initialState.burdeApiContent.selectedLayout = initialCalendarLayout;
    }

    if (initialFrontCoverImage) {
        initialState.calendar.coverImageId = initialFrontCoverImage.id;
    }

    if (initialCustomCoverImage) {
        initialState.calendar.customCoverImageId = initialCustomCoverImage.id;
        initialState.burdeApiContent.customCoverImage = initialCustomCoverImage;

        const found = initialState.ui.uploadedCoverImages.find(
            (x) => x.url === initialCustomCoverImage.url,
        );

        if (!found) {
            initialState.ui.uploadedCoverImages = [
                ...initialState.ui.uploadedCoverImages,
                initialCustomCoverImage,
            ];
        }
    }

    if (initialBackCoverImage) {
        initialState.calendar.backCoverImageId = initialBackCoverImage.id;
    }

    if (initialCustomBackImage) {
        initialState.calendar.customBackImageId = initialCustomBackImage.id;
        initialState.burdeApiContent.customBackImage = initialCustomBackImage;

        const found = initialState.ui.uploadedCoverImages.find(
            (x) => x.url === initialCustomBackImage.url,
        );

        if (!found) {
            initialState.ui.uploadedCoverImages = [
                ...initialState.ui.uploadedCoverImages,
                initialCustomBackImage,
            ];
        }
    }

    if (initialBorderImage) {
        initialState.calendar.borderImageId = initialBorderImage.id;
    }

    if (initialSpiral) {
        initialState.calendar.spiral = initialSpiral.articleNumber;
        initialState.ui.spiralItem = initialSpiral;
    }

    if (initialRubberBand) {
        initialState.calendar.rubberband = initialRubberBand.articleNumber;
        initialState.ui.rubberbandItem = initialRubberBand;
    }

    initialState.calendar.optionalDaySettings = {
        showFlagDays: configurationOptions.showFlagDays,
        showMoonPhases: configurationOptions.showMoonPhases,
        showNameDays: configurationOptions.showNameDays,
        showOtherDays: configurationOptions.showOtherDays,
        showRedDays: configurationOptions.showHolidays,
        showTimestamps: configurationOptions.showTimestamps,
    };

    if (fastTrack) {
        initialState.ui.FastTrack = fastTrack;
    }

    if (showLoginModal) {
        initialState.ui.ShowLoginModal = showLoginModal;
    }

    if (initialFrontCoverSectionId) {
        initialState.burdeApiContent.initialFrontCoverSectionId = initialFrontCoverSectionId;
    }

    if (initialBackCoverSectionId) {
        initialState.burdeApiContent.initialBackCoverSectionId = initialBackCoverSectionId;
    }

    if (calendarDefaults) {
        initialState.calendar = {
            ...initialState.calendar,
            ...calendarDefaults,
        };
    }

    if (postData) {
        const {
            name,
            calendarSize,
            calendarType,
            startDate,
            coverColor,
            backCoverColor,
            dayModules,
            weekModules,
            borderColor,
            lines,
            ownerPage,
            customNumberOfEndPages,
            monthDivider,
            extraPages,
            fontId,
            fontSetName,
            personalDateItems,
        } = postData;

        if (name) {
            initialState.calendar.name = name;
        }

        if (calendarSize) {
            initialState.calendar.calendarSize = calendarSize.toUpperCase();
        }

        if (calendarType) {
            initialState.calendar.calendarType = calendarType;
        }

        if (startDate) {
            initialState.calendar.startDate = moment(
                startDate,
                "YYYY-MM-DD",
            ).format("YYYY-MM-DD");
        }

        if (coverColor && !initialFrontCoverImage && !initialCustomCoverImage) {
            initialState.calendar.coverColor = coverColor;
        }

        if (
            backCoverColor &&
            !initialBackCoverImage &&
            !initialCustomBackImage
        ) {
            initialState.calendar.backCoverColor = backCoverColor;
        }

        if (dayModules) {
            initialState.calendar.dayModules = dayModules;
        }

        if (weekModules) {
            initialState.calendar.weekModules = weekModules;
        }

        if (borderColor && !initialBorderImage) {
            initialState.calendar.borderColor = borderColor;
        }

        if (lines) {
            initialState.calendar.lines = lines;
        }

        if (fontId) {
            initialState.calendar.fontId = fontId;
        }

        if (fontSetName) {
            initialState.calendar.fontSetName = fontSetName;
        }

        if (ownerPage) {
            if (ownerPage.layoutId) {
                initialState.calendar.ownerPage.layoutId = ownerPage.layoutId;
            }

            if (ownerPage.email) {
                initialState.calendar.ownerPage.email = ownerPage.email;
            }

            if (ownerPage.name) {
                initialState.calendar.ownerPage.name = ownerPage.name;
            }

            if (ownerPage.phoneNumber) {
                initialState.calendar.ownerPage.phoneNumber =
                    ownerPage.phoneNumber;
            }

            if (ownerPage.imageId) {
                initialState.calendar.ownerPage.imageId = ownerPage.imageId;
            }

            if (ownerPage.customImageId) {
                initialState.calendar.ownerPage.customImageId =
                    ownerPage.customImageId;
            }

            if (ownerPage.isGift) {
                initialState.calendar.ownerPage.isGift = ownerPage.isGift;
            }

            if (ownerPage.greeting) {
                initialState.calendar.ownerPage.greeting = ownerPage.greeting;
            }
        }

        if (
            customNumberOfEndPages &&
            initialState.calendar.calendarType === CalendarType.Notebook
        ) {
            initialState.calendar.customNumberOfEndPages = customNumberOfEndPages;
        }

        if (monthDivider) {
            if (monthDivider.layoutId) {
                initialState.calendar.monthDivider = {
                    layoutId: monthDivider.layoutId,
                    showFrame: monthDivider.showFrame,
                };

                if (
                    initialState.calendar.calendarType === CalendarType.Calendar
                ) {
                    initialState.calendar.customNumberOfEndPages = 12;
                }
            }
        }

        if (extraPages) {
            initialState.calendar.extraPages = extraPages;
        }

        if (postData.optionalDaySettings) {
            initialState.calendar.optionalDaySettings = {
                ...initialState.calendar.optionalDaySettings,
                ...postData.optionalDaySettings,
            };
        }

        if (jwt !== null) {
            Cookies.set(COOKIE_USER_JWT, jwt, {
                expires: COOKIE_USER_EXPIRE_DAYS,
                domain: initialState.ui.domain,
            });
        }

        if (personalDateItems) {
            initialState.calendar.personalDateItems = personalDateItems;
        }
    }

    initialState.ui.ActiveNavigationComponent = NavigationComponents[0];

    // Get default collection based on size and calendar type (always calendar for now)
    const defaultCoverText = getDefaultCoverTextByCalendarSizeAndCalendarType(
        configurationOptions.calendarSize,
        initialState.calendar.calendarType,
    );

    defaultCoverText.fontId = initialFontId;

    if (postData && postData.coverTexts) {
        const initialCoverTexts: Array<TextInformation> = [];

        for (let i = 0; i < postData.coverTexts.length; i++) {
            initialCoverTexts.push({
                ...defaultCoverText,
                ...{
                    text: postData.coverTexts[i].text,
                },
                ...(postData.coverTexts[i].color && {
                    color: postData.coverTexts[i].color,
                }),
                ...(postData.coverTexts[i].x && {
                    x: postData.coverTexts[i].x,
                }),
                ...(postData.coverTexts[i].y && {
                    y: postData.coverTexts[i].y,
                }),
                ...(postData.coverTexts[i].fontId && {
                    fontId: postData.coverTexts[i].fontId,
                }),
                ...(postData.coverTexts[i].fontSize && {
                    fontSize: postData.coverTexts[i].fontSize,
                }),
                ...(postData.coverTexts[i].rotation && {
                    rotation: postData.coverTexts[i].rotation,
                }),
            });
        }

        initialState.calendar.coverTexts = initialCoverTexts;
    } else if (!calendarDefaults || !calendarDefaults.coverTexts) {
        initialState.calendar.coverTexts = [defaultCoverText];
    }

    return configureStore(history, initialState);
}
